import axios from "axios";
import { useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { API_ROUTES } from "../api/routes";

export function useCdrPipeline() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const abortRef = useRef(null);

    const normalizeStatus = (s) => {
        const x = String(s || "").toLowerCase();
        if (x === "done") return "completed";
        if (x === "error") return "failed";
        if (x === "running") return "processing";
        return x;
    };

    /** Polling de estado del job */
    const pollJob = async (jobId, { interval = 2000, timeoutMs = 30 * 60 * 1000 } = {}) => {
        const started = Date.now();

        while (true) {
            if (abortRef.current?.aborted) {
                throw new Error("Operación cancelada");
            }

            try {
                // ✅ Ruta actualizada para CDR Pipeline
                const { data } = await axios.get(`${API_ROUTES.cdrPipeline.status}/${jobId}`);
                const status = normalizeStatus(data.status);

                if (status === "completed") return data;
                if (status === "failed") {
                    throw new Error(data.error_message || data.message || "Pipeline falló");
                }

                if (Date.now() - started > timeoutMs) {
                    throw new Error("Timeout: proceso tomó demasiado tiempo");
                }

                await new Promise(res => setTimeout(res, interval));
            } catch (e) {
                if (Date.now() - started < 10000) {
                    await new Promise(res => setTimeout(res, 1000));
                    continue;
                }
                throw e;
            }
        }
    };

    /** Subir archivos y procesar */
    const uploadAndProcess = async (files, opts = {}) => {
        setLoading(true);
        setError(null);
        setUploadProgress(0);
        abortRef.current = new AbortController();

        const formData = new FormData();

        // Agregar archivos al FormData
        Object.entries(files).forEach(([type, file]) => {
            if (file) {
                formData.append(type, file);
            }
        });

        // Agregar configuración de procesamiento
        formData.append('mode', opts.mode || 'full');
        formData.append('usePnnFromDB', opts.usePnnFromDB ? 'true' : 'false');
        formData.append('useTelegramBot', opts.useTelegramBot ? 'true' : 'false'); // ✅ NUEVO

        try {
            // Subir archivos
            const { data } = await axios.post(
                API_ROUTES.cdrPipeline.process,
                formData,
                {
                    signal: abortRef.current.signal,
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress: (ev) => {
                        if (ev.total) {
                            const pct = Math.round((ev.loaded * 100) / ev.total);
                            setUploadProgress(pct);
                            if (opts.onUploadProgress) {
                                opts.onUploadProgress(pct);
                            }
                        }
                    },
                }
            );

            const { ok, jobId } = data || {};
            if (!ok || !jobId) {
                throw new Error("Respuesta inválida del servidor");
            }

            toast.success("✓ Archivos subidos. Procesando pipeline...");

            // Callback con jobId
            if (opts.onJobCreated) {
                opts.onJobCreated(jobId);
            }

            // Polling del estado
            const result = await pollJob(jobId);
            
            // Mostrar notificación de éxito con información relevante
            if (result.year && result.month) {
                toast.success(
                    `✅ Pipeline completado\n` +
                    `📊 ${result.total_records?.toLocaleString() || 0} registros procesados\n` +
                    `📁 Tabla: contactacc${result.year}${result.month}`,
                    { duration: 5000 }
                );
            }

            return result;

        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "Error procesando";
            setError(msg);
            toast.error(msg);
            throw e;
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const cancel = () => {
        if (!abortRef.current) return;
        abortRef.current.aborted = true;
        abortRef.current.abort();
        toast.error("Operación cancelada");
    };

    return {
        uploadAndProcess,
        cancel,
        loading,
        error,
        uploadProgress,
        clearError: () => setError(null),
    };
}